import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  FiAlertTriangle,
  FiCheck,
  FiCheckCircle,
  FiMail,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiZap,
  FiX,
} from "react-icons/fi";
import toast from "react-hot-toast";

import {
  fetchEventWinners,
  markWinnerPaid,
  revertWinnerPayout,
  bulkApproveWinners,
  bulkRevertWinners,
  emailWinnerDetails,
  resendWinnerConfirmation,
} from "../../../utils/eventApi";
import { rewardsOf, normaliseCurrency, isAutoCredited } from "../../../utils/eventRewards";

/**
 * The coin approval queue for one event.
 *
 * Nothing is credited on its own. When an event is finalized the API opens one
 * `event_result_payout` row per winner per currency, every one of them
 * `pending`, and a person approves each.
 *
 * Approving **PX** credits it straight into the winner's balance and sends them
 * "your winning prize has been added to your PX account"; reverting it debits the
 * same amount back. Every other currency is an external transfer, so approving it
 * records a transaction id against that one currency — that is what a winner is
 * shown when they ask where their money went.
 *
 * PX can be approved or reverted in bulk — every winner in the event, or only the
 * ones ticked — and that runs server-side, so it covers every page of the queue.
 */

const getUserId = (row) => row?.user_id ?? row?.userId ?? row?.id ?? null;

/**
 * Does approving this currency mean recording an external transfer?
 *
 * PX is credited inside the platform, so there is no hash and asking for one
 * would only invite something made up. Everything else leaves through a wallet
 * or an exchange, and the reference is the only way to answer a winner asking
 * where their money went.
 */
const needsTransactionId = (currency) => !isAutoCredited(currency);

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d
        .toLocaleString("en-GB", {
          timeZone: "UTC",
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(",", "") + " UTC";
};

/**
 * Every reward owed to a winner, as rows that can be approved one at a time.
 *
 * The API sends `payouts` — the real per-currency state, with its own status
 * and transaction id. A winner finalized before that table existed has none, so
 * their prize is derived from the snapshot instead and shown as pending; better
 * a row that can be approved than a winner who silently is not listed.
 */
const payoutRowsOf = (winner) => {
  if (Array.isArray(winner.payouts) && winner.payouts.length > 0) {
    return winner.payouts.map((p) => ({
      currency: normaliseCurrency(p.currency),
      amount: Number(p.amount) || 0,
      status: p.status || "pending",
      paidAt: p.paid_at || null,
      // Approved before approval credited PX: the winner never got the coins.
      notCredited: Boolean(p.not_credited),
      // Exactly what approval added to the balance — and what a revert deducts.
      creditedAmount: Number(p.credited_amount) || 0,
      reference: p.reference || null,
      note: p.note || null,
      // USDT flow: email 1 asks for Binance details, email 2 confirms the transfer.
      detailsEmailedAt: p.details_emailed_at || null,
      confirmationEmailedAt: p.confirmation_emailed_at || null,
    }));
  }

  const alreadyPaid = winner.prize_status === "paid" || Boolean(winner.paid_at);
  return rewardsOf(winner).map((r) => ({
    currency: r.currency,
    amount: r.amount,
    status: alreadyPaid ? "paid" : "pending",
    paidAt: winner.paid_at || null,
    notCredited: false,
    reference: null,
    note: null,
    derived: true,
  }));
};

/** Still needs an approval: unpaid, or PX marked paid whose coins never landed. */
const needsApproval = (p) => p.status !== "paid" || p.notCredited;

const REWARD_FILTERS = [
  { key: "all", label: "All Rewards" },
  { key: "crypto", label: "Crypto Approvals" },
  { key: "px", label: "PX Rewards" },
];

const STATUS_FILTERS = [
  { key: "", label: "All Statuses" },
  { key: "pending", label: "Pending Approval" },
  { key: "paid", label: "Credited" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 6 });

/** A bulk result's failures, as one readable toast. */
const reportFailures = (failed) => {
  if (!Array.isArray(failed) || failed.length === 0) return;
  const shown = failed
    .slice(0, 5)
    .map((f) => `${f.username || f.user_id}: ${f.message}`)
    .join("; ");
  const more = failed.length > 5 ? ` …and ${failed.length - 5} more` : "";
  toast.error(`${failed.length} could not be processed — ${shown}${more}`, { duration: 10000 });
};

const ApprovalQueue = ({ eventId, eventStatus, onSummary }) => {
  const [winners, setWinners] = useState([]);
  const [serverSummary, setServerSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalWinners, setTotalWinners] = useState(0);

  const [rewardFilter, setRewardFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const [busy, setBusy] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [approving, setApproving] = useState(null);
  const [reference, setReference] = useState("");
  const [reverting, setReverting] = useState(null);
  const [reason, setReason] = useState("");

  // Ticked winners (by user id). PX is one row per winner, so a user id is
  // enough to name the reward a bulk action acts on.
  const [selected, setSelected] = useState(() => new Set());
  // Bulk revert asks for a reason first: { scope: "all" | "selected" }.
  const [bulkRevert, setBulkRevert] = useState(null);
  const [bulkReason, setBulkReason] = useState("");

  // Every winner in the event, unaffected by search — `totalWinners` is the
  // match count while a search or tab is narrowing the list.
  const [eventWinners, setEventWinners] = useState(null);

  // The search box, settled — so typing "virtu" is one request, not five.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Only the newest request may write to state; a slow earlier one that lands
  // after it would otherwise put stale results back on screen.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!eventId) {
      setWinners([]);
      setServerSummary([]);
      return;
    }

    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      // Search and both tab filters are applied by the API, over every winner
      // in the event — filtering here would only ever see the current page.
      const res = await fetchEventWinners(eventId, {
        page,
        limit: 100,
        search: debouncedSearch,
        reward: rewardFilter,
        payout_status: statusFilter,
      });
      if (seq !== requestSeq.current) return;
      if (res && res.status) {
        setWinners(res.data || []);
        setServerSummary(Array.isArray(res.summary) ? res.summary : []);
        setTotalWinners(res.pagination?.total ?? (res.data || []).length);
        if (typeof res.winners_total === "number") setEventWinners(res.winners_total);
        setTotalPages(res.pagination?.total_pages || 1);
      }
    } catch (err) {
      if (seq === requestSeq.current) toast.error("Failed to load the coin approval queue");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [eventId, page, debouncedSearch, rewardFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // A new event or a new filter starts from its first page.
  useEffect(() => {
    setPage(1);
  }, [eventId, debouncedSearch, rewardFilter, statusFilter]);

  // A selection only means something for the rows it was made on.
  useEffect(() => {
    setSelected(new Set());
    setBulkRevert(null);
  }, [eventId, page, debouncedSearch, rewardFilter, statusFilter]);

  /** Every winner with their reward rows attached, before any filtering. */
  const rows = useMemo(
    () => winners.map((winner) => ({ winner, payouts: payoutRowsOf(winner) })),
    [winners]
  );

  // Tab counts for the whole event, from the API's summary — the page only holds
  // 100 winners, so counting it would say "100" for an event with a thousand.
  // Falls back to this page for an API that does not send a summary.
  const counts = useMemo(() => {
    if (serverSummary.length > 0) {
      const sum = (list, pick) => list.reduce((n, s) => n + (Number(s[pick]) || 0), 0);
      const px = serverSummary.filter((s) => isAutoCredited(s.currency));
      const crypto = serverSummary.filter((s) => !isAutoCredited(s.currency));
      const pending = sum(serverSummary, "pending_count");
      const paid = sum(serverSummary, "paid_count");
      return {
        all: pending + paid,
        px: sum(px, "pending_count") + sum(px, "paid_count"),
        crypto: sum(crypto, "pending_count") + sum(crypto, "paid_count"),
        pending,
        paid,
      };
    }

    let all = 0;
    let crypto = 0;
    let px = 0;
    let pending = 0;
    let paid = 0;

    for (const { payouts } of rows) {
      for (const p of payouts) {
        all++;
        if (isAutoCredited(p.currency)) px++;
        else crypto++;
        if (p.status === "paid") paid++;
        else pending++;
      }
    }
    return { all, crypto, px, pending, paid };
  }, [rows, serverSummary]);

  /** Event-wide PX figures from the API — what the "all" bulk buttons act on. */
  const pxSummary = useMemo(() => {
    const row = serverSummary.find((s) => isAutoCredited(s.currency));
    return {
      pending: Number(row?.pending_count) || 0,
      pendingAmount: Number(row?.pending_amount) || 0,
      paid: Number(row?.paid_count) || 0,
      paidAmount: Number(row?.paid_amount) || 0,
      notCredited: Number(row?.not_credited_count) || 0,
    };
  }, [serverSummary]);

  /** Event-wide figures for each external-transfer currency (USDT, …). */
  const cryptoSummary = useMemo(
    () =>
      serverSummary
        .filter((s) => !isAutoCredited(s.currency))
        .map((s) => ({
          currency: s.currency,
          pending: Number(s.pending_count) || 0,
          pendingAmount: Number(s.pending_amount) || 0,
          paid: Number(s.paid_count) || 0,
          notEmailed: Number(s.details_not_emailed_count) || 0,
        })),
    [serverSummary]
  );

  useEffect(() => {
    if (!onSummary) return;

    // Prefer the event-wide figures; fall back to this page for an API that
    // does not send them yet.
    if (serverSummary.length > 0) {
      onSummary({
        totalWinners: eventWinners ?? totalWinners,
        pending: serverSummary.reduce((sum, s) => sum + (Number(s.pending_count) || 0), 0),
        paid: serverSummary.reduce((sum, s) => sum + (Number(s.paid_count) || 0), 0),
        owed: serverSummary
          .filter((s) => Number(s.pending_amount) > 0)
          .map((s) => ({ currency: s.currency, amount: Number(s.pending_amount) })),
      });
      return;
    }

    const owed = new Map();
    for (const { payouts } of rows) {
      for (const p of payouts) {
        if (p.status !== "paid") owed.set(p.currency, (owed.get(p.currency) || 0) + p.amount);
      }
    }
    onSummary({
      totalWinners,
      pending: counts.pending,
      paid: counts.paid,
      owed: [...owed.entries()].map(([currency, amount]) => ({ currency, amount })),
    });
    // onSummary is left out deliberately: a parent passing an inline arrow would
    // otherwise loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, counts, totalWinners, eventWinners, serverSummary]);

  const matchesReward = (p) =>
    rewardFilter === "all" ||
    (rewardFilter === "px" ? isAutoCredited(p.currency) : !isAutoCredited(p.currency));

  /** A search or a tab is narrowing the list — `totalWinners` is then the match count. */
  const filtering = Boolean(debouncedSearch) || rewardFilter !== "all" || Boolean(statusFilter);

  // "Pending" means anything not yet paid — the same rule the API filters by.
  const matchesStatus = (p) =>
    !statusFilter || (statusFilter === "paid" ? p.status === "paid" : p.status !== "paid");

  /**
   * The winners on this page, showing only the reward rows the tabs ask for.
   *
   * Which winners are here at all is the API's call — it has already applied the
   * search and the tabs across the whole event. This only trims a matched
   * winner's other rewards, so "PX Rewards" does not also show their USDT row.
   */
  const visible = rows
    .map(({ winner, payouts }) => ({
      winner,
      payouts: payouts.filter((p) => matchesReward(p) && matchesStatus(p)),
    }))
    .filter(({ payouts }) => payouts.length > 0);

  /** Visible winners who have a PX reward — the rows that can be ticked. */
  const selectable = visible
    .map(({ winner, payouts }) => ({
      userId: getUserId(winner),
      px: payouts.find((p) => isAutoCredited(p.currency)),
    }))
    .filter((r) => r.px && !r.px.derived);

  const pxByUser = useMemo(() => {
    const map = new Map();
    for (const { winner, payouts } of rows) {
      const px = payouts.find((p) => isAutoCredited(p.currency));
      if (px) map.set(String(getUserId(winner)), px);
    }
    return map;
  }, [rows]);

  const selectedIds = [...selected];
  const selectedToApprove = selectedIds.filter((id) => {
    const px = pxByUser.get(String(id));
    return px && needsApproval(px);
  });
  const selectedToRevert = selectedIds.filter((id) => {
    const px = pxByUser.get(String(id));
    return px && px.status === "paid";
  });

  const allSelectableTicked =
    selectable.length > 0 && selectable.every((r) => selected.has(String(r.userId)));

  const toggle = (userId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(userId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelectableTicked) {
        const next = new Set(prev);
        selectable.forEach((r) => next.delete(String(r.userId)));
        return next;
      }
      const next = new Set(prev);
      selectable.forEach((r) => next.add(String(r.userId)));
      return next;
    });
  };

  const busyAny = Boolean(busy) || bulkRunning;

  /**
   * Approve one currency for one winner.
   *
   * For PX this credits the balance there and then. For anything else the
   * transaction id goes with the request: recording the transfer and recording
   * what to point at are the same act.
   */
  const approve = async (winner, payout, txnReference) => {
    const key = `${getUserId(winner)}:${payout.currency}`;
    setBusy(key);
    try {
      const res = await markWinnerPaid(eventId, getUserId(winner), {
        currency: payout.currency,
        reference: txnReference,
      });
      const mailFailed = (res.data?.emails || []).filter((e) => !e.sent);
      if (mailFailed.length > 0) {
        // Approved, but the winner was not told — say so loudly so it gets resent.
        toast.error(res.message || "Approved, but the confirmation email failed", { duration: 8000 });
      } else {
        toast.success(res.message || `${payout.currency} approved`);
      }
      setApproving(null);
      setReference("");
      await load();
    } catch (err) {
      toast.error(err.message || `Could not approve ${payout.currency}`);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Take one approved reward back to pending.
   *
   * PX is debited from the winner's balance; the API refuses if the balance no
   * longer covers it. The reason is required because a reward that flips back
   * with no explanation is worse than one never approved.
   */
  const revert = async (winner, payout, why) => {
    const key = `${getUserId(winner)}:${payout.currency}`;
    setBusy(key);
    try {
      const res = await revertWinnerPayout(eventId, getUserId(winner), {
        currency: payout.currency,
        reason: why,
      });
      toast.success(res.message || `${payout.currency} moved back to pending`);
      setReverting(null);
      setReason("");
      await load();
    } catch (err) {
      toast.error(err.message || `Could not revert ${payout.currency}`);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Credit PX to many winners in one request.
   *
   * `userIds` omitted means every winner in the event that still needs it —
   * across every page, not only the one on screen. PX approved earlier whose
   * coins never landed is included, and the confirmation says so.
   */
  const runBulkApprove = async (userIds) => {
    const count = userIds ? userIds.length : pxSummary.pending + pxSummary.notCredited;
    if (count === 0) return;

    const scope = userIds
      ? `${count} selected winner${count === 1 ? "" : "s"}`
      : `ALL ${count} winner${count === 1 ? "" : "s"} still owed PX in this event`;
    const legacy =
      !userIds && pxSummary.notCredited > 0
        ? `\n\nThis includes ${pxSummary.notCredited} approved earlier whose coins were never added.`
        : "";

    if (
      !window.confirm(
        `Credit PX to ${scope}?${legacy}\n\nThe coins are added to their balance immediately and each winner is notified.`
      )
    ) {
      return;
    }

    setBulkRunning(true);
    try {
      const res = await bulkApproveWinners(eventId, {
        currency: "PX",
        userIds,
        includeNotCredited: true,
      });
      toast.success(res.message || "PX credited");
      reportFailures(res.data?.failed);
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err.message || "Bulk approval failed");
      reportFailures(err.response?.data?.failed);
    } finally {
      setBulkRunning(false);
    }
  };

  /**
   * USDT email 1 — "reply with your Binance UID and registered email".
   *
   * `winner` given: that one winner (sent again if already emailed). Omitted:
   * every pending winner of that currency in the event not yet emailed, across
   * all pages.
   */
  const sendDetailsEmail = async (currency, winner) => {
    if (!winner) {
      const pending = cryptoSummary.find((c) => c.currency === currency)?.notEmailed || 0;
      if (
        !window.confirm(
          `Email ${pending} ${currency} winner${pending === 1 ? "" : "s"} asking for their Binance UID and registered email?`
        )
      ) {
        return;
      }
    }

    const key = winner ? `${getUserId(winner)}:${currency}:mail` : `bulk:${currency}:mail`;
    setBusy(key);
    try {
      const res = await emailWinnerDetails(eventId, {
        currency,
        userIds: winner ? [getUserId(winner)] : undefined,
        resend: Boolean(winner),
      });
      toast.success(res.message || "Details email sent");
      reportFailures(res.data?.failed);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not send the details email");
      reportFailures(err.response?.data?.failed);
    } finally {
      setBusy(null);
    }
  };

  /** USDT email 2 again — the transfer confirmation with its order id. */
  const resendConfirmation = async (winner, payout) => {
    const key = `${getUserId(winner)}:${payout.currency}:mail`;
    setBusy(key);
    try {
      const res = await resendWinnerConfirmation(eventId, getUserId(winner), { currency: payout.currency });
      toast.success(res.message || "Confirmation email sent");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not send the confirmation email");
    } finally {
      setBusy(null);
    }
  };

  /** Take PX back from many winners in one request. */
  const runBulkRevert = async () => {
    if (!bulkRevert || !bulkReason.trim()) return;
    const userIds = bulkRevert.scope === "selected" ? selectedToRevert : undefined;

    setBulkRunning(true);
    try {
      const res = await bulkRevertWinners(eventId, {
        currency: "PX",
        userIds,
        reason: bulkReason.trim(),
      });
      toast.success(res.message || "PX reverted");
      reportFailures(res.data?.failed);
      setSelected(new Set());
      setBulkRevert(null);
      setBulkReason("");
      await load();
    } catch (err) {
      toast.error(err.message || "Bulk revert failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const chip = (active) =>
    `px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition border ${
      active
        ? "bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-600/20"
        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
    }`;

  const hasPx = pxSummary.pending + pxSummary.paid > 0;
  const toApproveAll = pxSummary.pending + pxSummary.notCredited;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="relative w-full lg:w-72">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all winners — username, email, user ID, rank…"
            className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              title="Clear search"
            >
              <FiX size={13} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {REWARD_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setRewardFilter(f.key)}
              className={chip(rewardFilter === f.key)}
            >
              {f.label} ({f.key === "all" ? counts.all : f.key === "px" ? counts.px : counts.crypto})
            </button>
          ))}

          <span className="w-px h-5 bg-gray-200 mx-1" />

          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || "any"}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={chip(statusFilter === f.key)}
            >
              {f.label}
              {f.key === "pending" ? ` (${counts.pending})` : f.key === "paid" ? ` (${counts.paid})` : ""}
            </button>
          ))}

          <button
            onClick={load}
            disabled={loading || bulkRunning || !eventId}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-xs font-medium rounded-lg transition flex items-center gap-1.5"
          >
            <FiRefreshCw size={13} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Say what approving does, where it is acted on. */}
      <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-2xl text-[11px] text-amber-900 flex items-start gap-2">
        <FiZap className="mt-0.5 shrink-0 text-amber-600" size={13} />
        <span>
          Every reward here is <strong>pending until approved</strong> — finalizing an event opens the
          queue, it does not credit anything. Approving <strong>PX</strong> adds the coins to the
          winner's balance immediately and notifies them; reverting PX deducts them again. Every other
          currency is an external transfer, so its transaction id is recorded when it is approved.
        </span>
      </div>

      {pxSummary.notCredited > 0 && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-2xl text-[11px] text-rose-800 flex items-start gap-2">
          <FiAlertTriangle className="mt-0.5 shrink-0 text-rose-600" size={13} />
          <span>
            <strong>{pxSummary.notCredited}</strong> PX reward
            {pxSummary.notCredited === 1 ? " was" : "s were"} approved before approval credited the
            balance, so {pxSummary.notCredited === 1 ? "that winner has" : "those winners have"} not
            received the coins. They are marked <em>Coins not added</em> below — use{" "}
            <strong>Credit PX</strong> on each, or <strong>Approve all pending PX</strong>.
          </span>
        </div>
      )}

      {/* USDT (external transfer) flow: 1. ask for Binance details, 2. approve with
          the transaction id — the confirmation email goes out on its own. */}
      {eventId &&
        cryptoSummary
          .filter((c) => c.pending + c.paid > 0)
          .map((c) => (
            <div
              key={c.currency}
              className="p-3 bg-emerald-50/50 border border-emerald-200 rounded-2xl flex flex-col lg:flex-row lg:items-center justify-between gap-3"
            >
              <span className="text-xs text-gray-600">
                <strong>{c.currency} in this event:</strong> {fmt(c.pending)} pending (
                {fmt(c.pendingAmount)} {c.currency}) · {fmt(c.paid)} transferred
                {c.notEmailed > 0 && (
                  <span className="text-amber-700"> · {c.notEmailed} not yet asked for Binance details</span>
                )}
                <span className="block text-[11px] text-gray-400 mt-0.5">
                  Step 1: email winners for their Binance UID. Step 2: approve each with its transaction id —
                  the confirmation email is sent automatically.
                </span>
              </span>
              <button
                onClick={() => sendDetailsEmail(c.currency)}
                disabled={busyAny || c.notEmailed === 0}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition flex items-center gap-1.5 whitespace-nowrap"
                title={`Email every pending ${c.currency} winner not yet asked for their details — all pages`}
              >
                <FiMail size={14} />
                {busy === `bulk:${c.currency}:mail`
                  ? "Sending…"
                  : `Email ${c.currency} winners for details (${c.notEmailed})`}
              </button>
            </div>
          ))}

      {/* PX bulk actions — event-wide, or the ticked winners. */}
      {eventId && hasPx && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-2xl space-y-3">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <span className="text-xs text-gray-600">
              <strong>PX in this event:</strong> {fmt(pxSummary.pending)} pending (
              {fmt(pxSummary.pendingAmount)} PX) · {fmt(pxSummary.paid)} credited (
              {fmt(pxSummary.paidAmount)} PX)
              {pxSummary.notCredited > 0 && (
                <span className="text-rose-600"> · {pxSummary.notCredited} coins not added</span>
              )}
            </span>

            <div className="flex items-center gap-2 flex-wrap">
              {bulkRunning && <span className="text-xs font-medium text-gray-500">Working…</span>}

              {selected.size > 0 ? (
                <>
                  <span className="text-xs text-gray-500">{selected.size} selected</span>
                  <button
                    onClick={() => runBulkApprove(selectedToApprove)}
                    disabled={busyAny || selectedToApprove.length === 0}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                  >
                    <FiCheckCircle size={14} /> Approve selected ({selectedToApprove.length})
                  </button>
                  <button
                    onClick={() => {
                      setBulkRevert({ scope: "selected" });
                      setBulkReason("");
                    }}
                    disabled={busyAny || selectedToRevert.length === 0}
                    className="px-3 py-1.5 bg-white border border-rose-200 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed text-rose-600 text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                  >
                    <FiRotateCcw size={13} /> Revert selected ({selectedToRevert.length})
                  </button>
                  <button
                    onClick={() => setSelected(new Set())}
                    disabled={bulkRunning}
                    className="px-2 py-1.5 text-gray-400 hover:text-gray-600 text-xs"
                  >
                    Clear
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => runBulkApprove(undefined)}
                    disabled={busyAny || toApproveAll === 0}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                    title="Credit PX to every winner in this event who has not received it — all pages"
                  >
                    <FiCheckCircle size={14} /> Approve all pending PX ({toApproveAll})
                  </button>
                  <button
                    onClick={() => {
                      setBulkRevert({ scope: "all" });
                      setBulkReason("");
                    }}
                    disabled={busyAny || pxSummary.paid === 0}
                    className="px-3 py-1.5 bg-white border border-rose-200 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed text-rose-600 text-xs font-semibold rounded-lg transition flex items-center gap-1.5"
                    title="Take PX back from every winner it was credited to — all pages"
                  >
                    <FiRotateCcw size={13} /> Revert all PX ({pxSummary.paid})
                  </button>
                </>
              )}
            </div>
          </div>

          {bulkRevert && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-xl">
              <span className="text-[11px] text-rose-800 flex-1">
                Revert PX for{" "}
                <strong>
                  {bulkRevert.scope === "selected"
                    ? `${selectedToRevert.length} selected winner${selectedToRevert.length === 1 ? "" : "s"}`
                    : `ALL ${pxSummary.paid} credited winner${pxSummary.paid === 1 ? "" : "s"}`}
                </strong>
                ? The coins are deducted from each balance and the winner is notified. Winners whose
                balance no longer covers it are skipped.
              </span>
              <input
                autoFocus
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && bulkReason.trim()) runBulkRevert();
                  if (e.key === "Escape") setBulkRevert(null);
                }}
                placeholder="Reason for revert (required)"
                className="w-full sm:w-56 px-2 py-1 border border-rose-300 rounded-lg text-[11px] focus:outline-none focus:ring-2 focus:ring-rose-500/20"
              />
              <div className="flex items-center gap-1.5">
                <button
                  onClick={runBulkRevert}
                  disabled={bulkRunning || !bulkReason.trim()}
                  className="px-3 py-1 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 text-white text-[11px] font-semibold rounded-lg transition"
                >
                  {bulkRunning ? "…" : "Revert"}
                </button>
                <button
                  onClick={() => setBulkRevert(null)}
                  disabled={bulkRunning}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <FiX size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-2xl">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="pl-4 pr-1 py-3 text-left w-8">
                <input
                  type="checkbox"
                  checked={allSelectableTicked}
                  onChange={toggleAll}
                  disabled={selectable.length === 0 || bulkRunning}
                  title="Select every PX winner in this view"
                  className="rounded border-gray-300 cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left">Rank</th>
              <th className="px-4 py-3 text-left">User info</th>
              <th className="px-4 py-3 text-left">Score</th>
              <th className="px-4 py-3 text-left">Reward</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Transaction ID</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {!eventId ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                  Pick an event to see what it owes.
                </td>
              </tr>
            ) : loading && winners.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                  Loading approval queue…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                  {eventStatus && eventStatus !== "completed"
                    ? "Nothing to approve yet — the queue is built when the event is finalized."
                    : filtering
                    ? `No winners in this event match ${
                        debouncedSearch ? `"${debouncedSearch}"` : "these filters"
                      }.`
                    : winners.length === 0
                    ? "This event has no winners."
                    : "No rewards match these filters."}
                </td>
              </tr>
            ) : (
              visible.map(({ winner, payouts }, winnerIdx) => {
                const userId = getUserId(winner);

                // The winner's own cells are written once and merged down their
                // rewards. Banding alternate winners is what makes a two-prize
                // block read as one person rather than two rows that lost their
                // name — a border alone is too easy to miss mid-table.
                const banded = winnerIdx % 2 === 1;
                const rowBg = banded ? "bg-gray-50/40" : "bg-white";
                const groupTop = winnerIdx > 0 ? "border-t-2 border-gray-100" : "";

                return payouts.map((payout, idx) => {
                  const key = `${userId}:${payout.currency}`;
                  const isPaid = payout.status === "paid";
                  const isApproving = approving === key;
                  const isFirst = idx === 0;
                  const isPx = isAutoCredited(payout.currency);
                  const topBorder = isFirst ? groupTop : "";

                  return (
                    <tr key={key} className={`${rowBg} align-top`}>
                      <td className={`pl-4 pr-1 py-3 ${topBorder}`}>
                        {isPx && !payout.derived && (
                          <input
                            type="checkbox"
                            checked={selected.has(String(userId))}
                            onChange={() => toggle(userId)}
                            disabled={bulkRunning}
                            className="rounded border-gray-300 cursor-pointer"
                          />
                        )}
                      </td>

                      {isFirst && (
                        <>
                          <td className={`px-4 py-3 ${groupTop}`} rowSpan={payouts.length}>
                            <span className="inline-flex items-center justify-center px-2 h-7 rounded-full text-xs font-bold bg-amber-100 text-amber-700 whitespace-nowrap">
                              #{winner.final_rank}
                            </span>
                          </td>

                          <td className={`px-4 py-3 ${groupTop}`} rowSpan={payouts.length}>
                            <p className="font-semibold text-gray-800">{winner.username || "—"}</p>
                            <p className="text-[11px] text-gray-400">{winner.email}</p>
                            {winner.zgram_id && (
                              <p className="text-[11px] text-blue-600 font-medium font-mono">
                                Zgram: {winner.zgram_id}
                              </p>
                            )}
                            {/* Say it outright, so two rows never look like two people. */}
                            {payouts.length > 1 && (
                              <p className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-semibold">
                                {payouts.length} prizes
                              </p>
                            )}
                          </td>

                          <td className={`px-4 py-3 ${groupTop}`} rowSpan={payouts.length}>
                            <span className="font-semibold text-gray-700">
                              {Number(winner.total_points || 0).toLocaleString()}
                            </span>
                          </td>
                        </>
                      )}

                      <td className={`px-4 py-3 ${topBorder}`}>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-lg font-bold border whitespace-nowrap ${
                            isPx
                              ? "bg-violet-50 text-violet-700 border-violet-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}
                        >
                          {payout.amount.toLocaleString()} {payout.currency}
                        </span>
                      </td>

                      <td className={`px-4 py-3 ${topBorder}`}>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            isPaid && payout.notCredited
                              ? "bg-rose-100 text-rose-700"
                              : isPaid
                              ? "bg-emerald-100 text-emerald-700"
                              : payout.status === "failed"
                              ? "bg-rose-100 text-rose-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {isPaid && payout.notCredited
                            ? "Coins not added"
                            : isPaid
                            ? "Credited"
                            : payout.status === "failed"
                            ? "Failed"
                            : "Pending"}
                        </span>
                        {payout.paidAt && (
                          <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(payout.paidAt)}</p>
                        )}
                        {/* Where this winner is in the two-email USDT flow. */}
                        {!isPx && !payout.derived && (
                          isPaid ? (
                            <p
                              className={`text-[10px] mt-0.5 flex items-center gap-1 ${
                                payout.confirmationEmailedAt ? "text-emerald-600" : "text-rose-600"
                              }`}
                            >
                              <FiMail size={10} />
                              {payout.confirmationEmailedAt
                                ? `Confirmation emailed ${formatDate(payout.confirmationEmailedAt)}`
                                : "Confirmation email not sent"}
                            </p>
                          ) : (
                            <p
                              className={`text-[10px] mt-0.5 flex items-center gap-1 ${
                                payout.detailsEmailedAt ? "text-emerald-600" : "text-amber-600"
                              }`}
                            >
                              <FiMail size={10} />
                              {payout.detailsEmailedAt
                                ? `Details requested ${formatDate(payout.detailsEmailedAt)}`
                                : "Binance details not requested"}
                            </p>
                          )
                        )}
                      </td>

                      <td className={`px-4 py-3 max-w-[180px] ${topBorder}`}>
                        {payout.reference ? (
                          <span
                            className="font-mono text-[11px] text-gray-600 break-all"
                            title={payout.reference}
                          >
                            {payout.reference}
                          </span>
                        ) : needsTransactionId(payout.currency) ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          // Not missing data: PX never has one.
                          <span className="text-[10px] text-gray-400 italic">Internal credit</span>
                        )}
                      </td>

                      <td className={`px-4 py-3 text-right ${topBorder}`}>
                        {isPaid && reverting === key ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <input
                              autoFocus
                              value={reason}
                              onChange={(e) => setReason(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && reason.trim()) {
                                  revert(winner, payout, reason.trim());
                                }
                                if (e.key === "Escape") setReverting(null);
                              }}
                              placeholder={
                                isPx && payout.creditedAmount > 0
                                  ? `Reason (deducts ${payout.creditedAmount.toLocaleString()} PX)`
                                  : isPx
                                  ? "Reason (no coins were added, nothing deducted)"
                                  : "Reason for revert"
                              }
                              className="w-44 px-2 py-1 border border-rose-300 rounded-lg text-[11px] focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                            />
                            <button
                              onClick={() => revert(winner, payout, reason.trim())}
                              disabled={busy === key || !reason.trim()}
                              className="px-2 py-1 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 text-white font-semibold rounded-lg transition"
                              title={
                                isPx
                                  ? "Deduct this PX from the winner's balance and move it back to pending"
                                  : "Move this reward back to pending"
                              }
                            >
                              {busy === key ? "…" : "Revert"}
                            </button>
                            <button
                              onClick={() => setReverting(null)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                        ) : isPaid ? (
                          <div className="flex items-center justify-end gap-2">
                            {payout.notCredited ? (
                              <button
                                onClick={() => approve(winner, payout)}
                                disabled={busyAny}
                                className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 text-rose-700 font-semibold rounded-lg transition border border-rose-200 whitespace-nowrap"
                                title="This PX was approved but never added to the balance — credit it now"
                              >
                                {busy === key ? "…" : "Credit PX"}
                              </button>
                            ) : (
                              <span className="text-emerald-600 flex items-center gap-1">
                                <FiCheck size={14} /> Settled
                              </span>
                            )}
                            {!isPx && payout.reference && (
                              <button
                                onClick={() => resendConfirmation(winner, payout)}
                                disabled={busyAny}
                                className="p-1 text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 rounded transition"
                                title={
                                  payout.confirmationEmailedAt
                                    ? "Resend the transfer confirmation email"
                                    : "Send the transfer confirmation email"
                                }
                              >
                                {busy === `${key}:mail` ? "…" : <FiMail size={13} />}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setReverting(key);
                                setReason("");
                              }}
                              disabled={busyAny}
                              className="p-1 text-gray-300 hover:text-rose-600 hover:bg-rose-50 rounded transition"
                              title={`Take the ${payout.currency} back to pending`}
                            >
                              <FiRotateCcw size={13} />
                            </button>
                          </div>
                        ) : isApproving ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <input
                              autoFocus
                              value={reference}
                              onChange={(e) => setReference(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && reference.trim()) {
                                  approve(winner, payout, reference.trim());
                                }
                                if (e.key === "Escape") setApproving(null);
                              }}
                              placeholder="Transaction ID"
                              className="w-36 px-2 py-1 border border-emerald-300 rounded-lg text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                            />
                            <button
                              onClick={() => approve(winner, payout, reference.trim())}
                              disabled={busy === key || !reference.trim()}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-semibold rounded-lg transition"
                              title="Record this transfer"
                            >
                              {busy === key ? "…" : "Save"}
                            </button>
                            <button
                              onClick={() => setApproving(null)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1.5">
                            {/* USDT step 1: ask for the Binance details. */}
                            {!isPx && !payout.derived && (
                              <button
                                onClick={() => sendDetailsEmail(payout.currency, winner)}
                                disabled={busyAny}
                                className="px-2 py-1 bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-600 font-medium rounded-lg transition border border-gray-200 whitespace-nowrap flex items-center gap-1"
                                title="Email this winner asking for their Binance UID and registered email"
                              >
                                <FiMail size={12} />
                                {busy === `${key}:mail`
                                  ? "…"
                                  : payout.detailsEmailedAt
                                  ? "Resend"
                                  : "Ask details"}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                // PX has nothing to reference, so it goes straight
                                // through rather than opening an empty box.
                                if (!needsTransactionId(payout.currency)) {
                                  approve(winner, payout);
                                  return;
                                }
                                setApproving(key);
                                setReference("");
                              }}
                              disabled={busyAny}
                              className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 text-emerald-700 font-semibold rounded-lg transition border border-emerald-200 whitespace-nowrap"
                              title={
                                isPx
                                  ? "Add this PX to the winner's balance"
                                  : "Record the transfer with its transaction id — the confirmation email is sent automatically"
                              }
                            >
                              {busy === key ? "…" : `Approve ${payout.currency}`}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                });
              })
            )}
          </tbody>
        </table>
      </div>

      {filtering && totalPages <= 1 && eventId && !loading && (
        <p className="text-xs text-gray-400">
          {totalWinners} winner{totalWinners === 1 ? "" : "s"} match{totalWinners === 1 ? "es" : ""} across the
          whole event
        </p>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Page {page} of {totalPages} · {totalWinners} {filtering ? "matching " : ""}winners
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1 || loading || bulkRunning}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs disabled:opacity-40 hover:bg-gray-50 transition"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages || loading || bulkRunning}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs disabled:opacity-40 hover:bg-gray-50 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalQueue;
